import * as React from 'react';
import styled, {css, keyframes} from 'styled-components';
import {transparentize, lighten} from 'polished';
import {StyledContainer} from 'containers/Container';
import {IconClose} from 'icons';
import {transition, outline} from 'helpers/css';
import useLockBodyScroll from 'helpers/useLockBodyScroll';
import useClickOutside from 'helpers/useClickOutside';
import scrollStyles from "helpers/css/scroll";

export interface ModalProps {
  children: React.ReactNode | React.ReactNodeArray;
  renderHeader?: React.ReactNode | React.ReactNodeArray;
  renderFooter?: React.ReactNode | React.ReactNodeArray;
  variant?: ModalVariant;
  fullScreen?: boolean;
  onBlur?(): void;
}

export enum ModalVariant {
  Default = 'default',
  Accent = 'accent',
}

const OverlayAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 999;
  animation: ${OverlayAnimation} .28s ease-out;
  background: rgba(0, 0, 0, 0.85);
`;

export const Container = styled(StyledContainer)`
  height: 100vh;
  padding-top: 15px;
  padding-bottom: 15px;
  justify-content: center;
  flex-direction: column;
  display: flex;
  
  @media screen and (min-width: 768px) {
    padding-top: 30px;
    padding-bottom: 30px;
  }
`;

const WrapperAnimation = keyframes`
  from {
    transform: translateY(-30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

export const Wrapper = styled.div<Pick<ModalProps, 'fullScreen' | 'variant'>>`
  max-height: 100%;
  height: ${({fullScreen}) => fullScreen && '100%'};
  justify-content: ${({fullScreen}) => !fullScreen && 'center'};
  flex-direction: column;
  display: flex;
  border-radius: 5px;
  animation: ${WrapperAnimation} .15s ease-out;

  ${({variant, theme}) => {
    switch (variant) {
      case ModalVariant.Default: return css`
        background-color: ${theme.variables.backgroundColor};
      `;
      case ModalVariant.Accent: return css`
        background-color: ${theme.variables.accentBackgroundColor};
      `;
    }
  }};
  
  &:focus-visible {
    ${outline(6, 2)};
  }
`;

export const Header = styled.div`
  position: relative;
  flex-shrink: 0;
  box-sizing: border-box;
  border-radius: 5px 5px 0 0;
  padding: 15px;
  width: 100%;
  background: ${({theme}) => `
    ${lighten(.02, theme.variables.backgroundColor)};
  `};
  
  @media screen and (min-width: 768px) {
    padding: 20px 30px;
  }
  
  #modal-close-button {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    border-radius: 0 5px 0 0;
  }
`;

export const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  
  @media screen and (min-width: 768px) {
    font-size: 18px;
  }
`;

export const CloseButton = styled.button`
  padding: 0 22px;
  ${transition()};
  color: ${({theme}) => `
    ${transparentize(.8, theme.colors.white)};
  `};
  
  svg {
    fill: currentColor;
  }
  
  &:hover,
  &:focus-visible {
    color: ${({theme}) => `
      ${transparentize(.5, theme.colors.white)};
    `};
  }
  
  &:focus-visible {
    ${outline(6, 2)};
  }
  
  &:active {
    background: ${({theme}) => `
      ${transparentize(.95, theme.colors.white)};
    `};
  }
`;

export const Content = styled.div<Pick<ModalProps, 'fullScreen'>>`
  overflow-y: auto;
  box-sizing: border-box;
  padding: 0 15px;
  height: ${({fullScreen}) => fullScreen && '100%'};
  max-height: 100%;
  width: 100%;
  
  ${scrollStyles()};
  
  @media screen and (min-width: 768px) {
    padding: 0 30px;
  }
`;

export const Footer = styled.div`
  flex-shrink: 0;
  text-align: right;
  border-radius: 0 0 5px 5px;
  box-sizing: border-box;
  padding: 15px;
  width: 100%;
  
  @media screen and (min-width: 768px) {
    padding: 20px 30px;
  }
`;

/**
 * The modal component
 * @param {ModalProps} props
 * @return {React.ReactElement<ModalProps>}
 * @constructor
 */
function Modal(
    props: ModalProps,
): React.ReactElement<ModalProps> {
  useLockBodyScroll();

  const wrapperRef = React.createRef<HTMLDivElement>();
  useClickOutside(wrapperRef, () => {
    props.onBlur && props.onBlur();
  });

  /**
   * Render close button
   * @return {JSX.Element}
   */
  function renderCloseButton() {
    /**
     * Handle click close button
     */
    function handleClick(): void {
      props.onBlur && props.onBlur();
    }
    return (
      <CloseButton
        id="modal-close-button"
        onClick={handleClick}
      >
        <IconClose />
      </CloseButton>
    );
  }
  return (
    <Overlay>
      <Container>
        <Wrapper
          ref={wrapperRef}
          tabIndex={0}
          fullScreen={props.fullScreen}
          variant={props.variant}
        >
          {props.renderHeader && (
            <Header>
              {props.renderHeader}
              {renderCloseButton()}
            </Header>
          )}
          <Content fullScreen={props.fullScreen}>
            {props.children}
          </Content>
          {props.renderFooter && (
            <Footer>
              {props.renderFooter}
            </Footer>
          )}
        </Wrapper>
      </Container>
    </Overlay>
  );
}

Modal.defaultProps = {
  variant: ModalVariant.Default,
};

export default Modal;
